import {
	Grid,
	useTheme,
	Box,
	Button,
	IconButton,
	Divider,
	Paper,
	Tooltip,
	Typography,
	Tabs,
	Tab,
	darken,
	lighten,
} from "@mui/material";
import useStyles from "./styles";
import { memo, useEffect, useRef, useState } from "react";
import InternoCircuitoCliente from "./circuito";
import { AddOutlined, HttpsOutlined, WidgetsOutlined } from "@mui/icons-material";
import api from "../../../../services/api";
import PreviewData from "./previewData";
import InternoModalConfirmacaoSaida from "../../../../components/modalSaindoDaPagina";
import labelCircuito from "./labelCircuito";
import ModalTemplates from "./circuito/diagramaEquipamentos/templates";

// Rótulos das três abas do circuito aberto, na mesma ordem dos índices de abaAtiva (0 Formulário, 1 Anexos, 2 Diagrama de equipamentos)
const LABELS_ABA_CIRCUITO = ["Formulário", "Anexos", "Diagrama de equipamentos"];

const ServiceItem = memo(
	({
		servico,
		ownerClienteId,
		otherOptions,
		popTags,
		regionalTags,
		loadingTags,
		updateOptions,
		optionsRoteamento,
		optionsEntregaFinal,
		optionsEntregaFinalCliente,
		setOptionsEntregaFinal,
		setOptionsRoteamento,
		setOptionsEntregaFinalCliente,
		onFormUpdate,
		changed,
		setChanged,
		forms,
		setForms,
		editMode,
		setEditMode,
		selectedTitle,
		setSnackbar,
		clientCpes,
		clientHosts,
		// Circuito atualmente selecionado na lista lateral (dono do estado é index.js)
		selectedCircuitKey,
		// Criação de circuito: lógica mora em index.js, acessível também sem este serviço estar aberto (menu do CardSelection)
		onAdicionarCircuito,
		// Remoção de circuito: estado e ações também moram em index.js (dono de forms/selectCliente)
		confirmRemoveCircuit,
		loadingRemoveCircuit,
		onConfirmRemove,
		onCancelRemove,
		// Repassa o loading do circuito aberto pra index.js poder bloquear a saída com save em voo (Task 7 fix)
		onCircuitoLoadingChange,
	}) => {
		const theme = useTheme();
		const styles = useStyles(theme);
		const [showAllContacts, setShowAllContacts] = useState(true);
		const formCorrespondente = forms?.[servico];
		// Handlers/loading do circuito aberto: reportados por circuito/index.js (Task 7, sem ref)
		const [circuitoActions, setCircuitoActions] = useState({
			handleSave: null,
			handleReset: null,
			loading: false,
			abaAtiva: 0,
			onTrocarAba: null,
		});
		const effectTriggeredRef = useRef(false);
		const fetchedSerialsRef = useRef({});
		const [loadingSerial, setLoadingSerial] = useState(false);
		// Aba ativa própria do preview: circuitoActions.abaAtiva só existe com InternoCircuitoCliente (edição) montado
		const [previewAbaAtiva, setPreviewAbaAtiva] = useState(0);
		// Controle do modal de templates elevado para fora do DiagramaCanvas
		const [templatesModalOpen, setTemplatesModalOpen] = useState(false);

		// Reseta a aba do preview ao trocar de circuito selecionado, senão abre na aba que ficou ativa no anterior
		useEffect(() => {
			setPreviewAbaAtiva(0);
		}, [selectedCircuitKey]);

		// Espelha o loading do circuito aberto pra index.js (guarda de navegação, ver handleConfirmNavigation); cleanup zera se o ServiceItem desmontar (troca de serviço) com um save em voo
		useEffect(() => {
			onCircuitoLoadingChange?.(circuitoActions.loading);
			return () => onCircuitoLoadingChange?.(false);
		}, [circuitoActions.loading, onCircuitoLoadingChange]);

		useEffect(() => {
			// Espera changed=false: com alteração pendente, onAdicionarCircuito adiaria a criação atrás de um modal de confirmação sem ação do usuário, travando effectTriggeredRef sem criar nada
			// forceEditMode: false — criação silenciosa (usuário só selecionou o serviço, não pediu edição): abre em preview, sem marcar `changed`
			if (!forms?.[servico] && !effectTriggeredRef.current && !changed) {
				setShowAllContacts(true);
				onAdicionarCircuito?.(servico, undefined, { forceEditMode: false });
				effectTriggeredRef.current = true;
			}
		}, [servico, changed]);

		useEffect(() => {
			const fetchSerialIfNeeded = async () => {
				const circuits = forms?.[servico]?.circuits;
				if (!circuits || circuits.length === 0) return;

				if (fetchedSerialsRef.current[servico]) return;

				const hasCircuitWithoutSerial = circuits.some((c) => !c?.serial);
				if (!hasCircuitWithoutSerial) return;

				fetchedSerialsRef.current[servico] = true;
				setLoadingSerial(true);

				try {
					const response = await api.post("/interno/findMany/oltClientServico", {
						id_cliente_servico: servico,
					});

					const serialVal = response?.data;
					if (serialVal) {
						setForms((prev) => {
							const newForms = { ...prev };
							const updatedCircuits = newForms[servico]?.circuits?.map((c) => {
								if (!c?.serial) {
									return { ...c, serial: serialVal };
								}
								return c;
							});
							newForms[servico] = {
								...newForms[servico],
								circuits: updatedCircuits,
							};
							return newForms;
						});
					}
				} catch (err) {
					console.error("Erro ao carregar serial do ONU:", err);
				} finally {
					setLoadingSerial(false);
				}
			};

			if (forms?.[servico]) {
				fetchSerialIfNeeded();
			}
		}, [servico, forms?.[servico]?.circuits, setForms]);

		const indexSelecionado = formCorrespondente?.circuits?.findIndex(
			(circuit) => (circuit?.id || circuit?.tempId) === selectedCircuitKey,
		);
		const circuitoSelecionado =
			indexSelecionado >= 0 ? formCorrespondente?.circuits?.[indexSelecionado] : null;

		return (
			<Grid item xs={12} key={servico}>
				<Box sx={styles.containerServiceItem}>
					{/* Botões */}
					<Box sx={styles.headStcker}>
						<Box sx={styles.boxButtons}>
							<Box>
								<Typography variant="body2" fontWeight={700}>
									{selectedTitle}
								</Typography>
								{/* Nome/rótulo do circuito aberto no painel, além do subitem já destacado na lista lateral (§4 da spec) */}
								{circuitoSelecionado && (
									<Typography variant="caption" color="text.secondary">
										{labelCircuito(circuitoSelecionado, indexSelecionado)}
									</Typography>
								)}
							</Box>
							<Box sx={styles.headerActions}>
								{editMode && (
									<IconButton
										size="small"
										disabled={circuitoActions.loading}
										onClick={() => {
											setShowAllContacts(true);
											onAdicionarCircuito?.(servico);
										}}
										sx={styles.addTabButton}
										title="Adicionar circuito"
									>
										<AddOutlined fontSize="small" />
									</IconButton>
								)}
								{editMode ? (
									<Box sx={{ display: "flex", gap: 1, flexDirection: "row" }}>
										<Button
											disabled={circuitoActions.loading}
											variant="outlined"
											onClick={() => circuitoActions.handleReset?.()}
											size="small"
										>
											Cancelar
										</Button>
										<Button
											disabled={circuitoActions.loading}
											variant="contained"
											color="primary"
											onClick={() => circuitoActions.handleSave?.()}
											size="small"
										>
											Salvar
										</Button>
									</Box>
								) : (
									<Box sx={{ display: "flex", gap: 1, flexDirection: "row", alignItems: "center" }}>
										{/* Botão de templates acessível fora do modo de edição (aplicação em massa) */}
										<Tooltip title="Aplicar template de diagrama em vários circuitos" arrow>
											<Button
												variant="outlined"
												size="small"
												startIcon={<WidgetsOutlined fontSize="small" />}
												onClick={() => setTemplatesModalOpen(true)}
												aria-label="Templates de diagrama"
											>
												Templates
											</Button>
										</Tooltip>
										<Button
											variant="contained"
											size="small"
											startIcon={<HttpsOutlined />}
											onClick={() => {
												setEditMode(true);
												setChanged(true);
												setShowAllContacts(true);
											}}
										>
											Editar
										</Button>
									</Box>
								)}
							</Box>
						</Box>
						{/* Abas do circuito aberto (Formulário/Anexos/Diagrama), fora do Paper — mesmo lugar/estilo das antigas abas "Circuito 1..N"; visíveis tanto em edição quanto em preview, cada modo com seu próprio estado de aba ativa */}
						{circuitoSelecionado && (
							<Box sx={styles.containerTabsNActionButtons}>
								<Tabs
									value={editMode ? circuitoActions.abaAtiva : previewAbaAtiva}
									onChange={(event, newValue) =>
										editMode
											? circuitoActions.onTrocarAba?.(newValue)
											: setPreviewAbaAtiva(newValue)
									}
									sx={styles.tabs}
									variant="scrollable"
									scrollButtons="auto"
									allowScrollButtonsMobile
								>
									{LABELS_ABA_CIRCUITO.map((label, index) => {
										const abaAtivaAtual = editMode
											? circuitoActions.abaAtiva
											: previewAbaAtiva;
										return (
											<Tab
												key={label}
												label={label}
												sx={{
													...styles.tab,
													...(abaAtivaAtual === index && {
														borderColor: "divider",
														backgroundColor: theme.palette.background.paper,
													}),
													...(abaAtivaAtual !== index && {
														backgroundColor:
															theme.palette.mode === "dark"
																? darken(theme.palette.background.paper, 0.05)
																: lighten(theme.palette.secondary.main, 0.95),
													}),
													"&:hover": {
														borderColor:
															abaAtivaAtual === index
																? "divider"
																: darken(theme.palette.background.paper, 0.5),
														backgroundColor:
															abaAtivaAtual === index
																? "background.paper"
																: lighten(theme.palette.background.paper, 0.05),
													},
												}}
											/>
										);
									})}
							</Tabs>
							</Box>
						)}
						<Divider />
					</Box>

					{circuitoSelecionado && (
						<Paper
							variant="outlined"
							sx={{
								p: 1,
								borderRadius: 1,
								mt: 2,
							}}
						>
							{editMode ? (
								<InternoCircuitoCliente
									// key força remount ao trocar de circuito selecionado — sem isso o
									// React reaproveitava a instância e formData/diagrama ficavam presos no circuito anterior
									key={selectedCircuitKey}
									serviceId={servico}
									ownerClienteId={ownerClienteId}
									isActive={true}
									index={indexSelecionado}
									data={circuitoSelecionado}
									delivery={otherOptions}
									popTags={popTags}
									regionalTags={regionalTags}
									loadingTags={loadingTags}
									loadingSerial={loadingSerial}
									serviceData={servico}
									setCircuitoActions={setCircuitoActions}
									updateOptions={updateOptions}
									optionsRoteamento={optionsRoteamento}
									optionsEntregaFinal={optionsEntregaFinal}
									optionsEntregaFinalCliente={optionsEntregaFinalCliente}
									setOptionsEntregaFinal={setOptionsEntregaFinal}
									setOptionsRoteamento={setOptionsRoteamento}
									setOptionsEntregaFinalCliente={setOptionsEntregaFinalCliente}
									onFormUpdate={onFormUpdate}
									setChanged={setChanged}
									editMode={editMode}
									setEditMode={setEditMode}
									setShowAllContacts={setShowAllContacts}
									showAllContacts={showAllContacts}
									clientCpes={clientCpes}
									clientHosts={clientHosts}
									abaInicialEditar={previewAbaAtiva}
								/>
							) : (
								<PreviewData
									key={selectedCircuitKey}
									form={circuitoSelecionado}
									setSnackbar={setSnackbar}
									clientCpes={clientCpes}
									clientHosts={clientHosts}
									servico={servico}
									isActive={true}
									abaAtiva={previewAbaAtiva}
								/>
							)}
						</Paper>
					)}
				</Box>
				<InternoModalConfirmacaoSaida
					openConfirmacao={confirmRemoveCircuit}
					deleteLoad={loadingRemoveCircuit}
					setOpenConfirmacao={onCancelRemove}
					execute={onConfirmRemove}
					severity={"error"}
					title={"Atenção"}
					contentText={
						"Deseja remover o circuito? Se sim, os dados serão permanentemente perdidos"
					}
					confirmText={"Continuar"}
				/>
				{/* Modal de templates elevado: acessível sem editMode, modo massa habilita só aplicação em vários */}
				<ModalTemplates
					open={templatesModalOpen}
					onClose={() => setTemplatesModalOpen(false)}
					editMode={editMode}
					modo="massa"
					setSnackbar={setSnackbar}
				/>
			</Grid>
		);
	},
);

export { ServiceItem };
